import React, { useState } from 'react';
import { Container, Grid, Button, Typography }from '@material-ui/core';
import { Card, CardContent, CardActions, TextField } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Alert } from '@material-ui/lab';
import { SniffKey } from '../api/api';
import { SniffKeyResponse } from '../models/sniffKey.interface';
import NumberFormat from 'react-number-format';
import { KeyTypes } from '../app/enums'
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';

const useStyles = makeStyles((theme: Theme) =>
  createStyles ({
    container: {
      padding: 20,
    },
    pageTitle: {
      color: theme.palette.primary.dark,
      fontWeight: 'bold',
    },
    gridSpacer: {
      minHeight: '12px',
    },
    gridLine: {
      flexGrow: 1,
      margin: theme.spacing(1, 0, 1),
    },
    card: {
      marginLeft: theme.spacing(1),
      width: '100%'
    },
    button: {
      margin: theme.spacing(0, 0, 0, 1),
    },
    alert: {
      width: '100%',
      margin: theme.spacing(1, 0, 1, 1),
    },
  }),
);

export const AdminSearch = () => {
  const classes = useStyles(); 
  const [ vin, setVin ] = useState('');
  const [ vinError, setVinError] = useState('')
  const [ sniffKeyResult, setSniffKeyResult ] = useState<SniffKeyResponse>();
  const [ loading , setLoading ] = useState(false);

  const handleChange = (event: any) => {
    setVin(event.currentTarget.value);
    setSniffKeyResult(undefined);
  };
   
  const handleEnter = (event: React.KeyboardEvent) => {
    if(event.key === 'Enter'){
      callSearch();
    }
  }

  const callSearch = () => {
    setLoading(true)
    setVinError('')
    setSniffKeyResult(undefined);

    if(vin === '' || vin === undefined){
      setVinError('Undefined')
      setLoading(false);
      return;
    }

    SniffKey.sniffKey(vin)
      .then((searchData) => {
        setSniffKeyResult(searchData)
        setLoading(false);
      }).catch((e) => {
        setVinError(e.response.data.message)
        setSniffKeyResult(undefined);
        setLoading(false);
      })
  }

  const clearSearch = () => {
    setVin('');
    setVinError('');
    setSniffKeyResult(undefined);
  }

  return (
    <Container 
      aria-label="adminSearchContainer"
      component="main" 
      maxWidth="md" 
      className={classes.container}
    >
      <Grid container justifyContent="flex-start" alignItems="flex-start">
        <Typography 
          aria-label="adminSearchTitle"
          className={classes.pageTitle}
          variant="h5"
        >
          Search
        </Typography>
      </Grid>
      <Grid container className={classes.gridSpacer} />
      <Grid container spacing={0}>
        <Card className={classes.card}>
          <CardContent>
            <Typography className={classes.pageTitle}>
              Enter VIN for Quote:
            </Typography>
            <Grid container className={classes.gridLine}>
              <TextField
                aria-label="vin"
                id="vin"
                label="VIN"
                fullWidth
                variant="outlined"
                size="medium"
                required
                onChange={(e) => handleChange(e)}
                onPaste={(e) => handleChange(e)}
                onKeyPress={(e) => handleEnter(e)}
                value={vin}
                color="primary"
                inputProps={{ "data-testid": "vinInput" }}
              />
            </Grid>
          </CardContent>
          <CardActions>
            <Grid container 
              justifyContent="flex-end"
            >
              <Button 
                aria-label="clearButton"
                onClick={clearSearch}
                color="primary"
                variant="outlined"
                className={classes.button}
              >
                Clear
              </Button>
              <Button 
                aria-label="searchButton"
                variant="contained"
                color="primary"
                size="medium"
                startIcon={<SearchIcon/>}
                onClick={callSearch}
                className={classes.button}
              >
                Search
              </Button> 
            </Grid>
          </CardActions>
        </Card>
      </Grid>
      <Grid container className={classes.gridSpacer} /> 
      <Grid 
        aria-label="resultGrid"
        container 
        spacing={0} 
        justifyContent="center"
        alignItems="center"
      > 
        { loading && 
          <CircularProgress 
            aria-label="loadingCircle"
            color="primary" 
          /> 
        }
        { sniffKeyResult && 
          <Card 
            className={classes.card}
            aria-label="resultCard"
          >
            <CardContent>
              <Grid container>       
                { sniffKeyResult &&
                  <Typography 
                    style={{ fontWeight: 600 }}
                    aria-label="vehicleInfo"  
                  >
                    {sniffKeyResult.year} {sniffKeyResult.make} {sniffKeyResult.model}
                  </Typography>
                }  
              </Grid>
              <Grid container className={classes.gridSpacer} />
              <Grid 
                container
                justifyContent="space-between"
                aria-label="resultHeader"
              >  
                <Grid 
                  container
                  justifyContent="center"
                  item xs={2}
                >
                  <Typography className={classes.pageTitle}> 
                    Company
                  </Typography>
                </Grid>
                <Grid 
                  container
                  justifyContent="center"
                  item xs={2}
                >
                  <Typography className={classes.pageTitle}>
                    Key Type
                  </Typography>
                </Grid>
                <Grid 
                  container
                  justifyContent="center"
                  item xs={1}
                >
                  <Typography className={classes.pageTitle}>
                    Price
                  </Typography>
                </Grid>
                <Grid 
                  container
                  justifyContent="center"
                  item xs={6}
                >
                  <Typography className={classes.pageTitle}>
                    Report
                  </Typography>
                </Grid>
              </Grid>
              <Grid container className={classes.gridSpacer} />
              { sniffKeyResult.prices.map(item => (
                <Grid 
                  container
                  justifyContent="space-between"
                  aria-label="pricesGrid"
                  key={item.companyName}
                >  
                  <Grid item xs={2}>
                    <Typography> 
                      {item.companyName}
                    </Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography>
                      {KeyTypes[item.keyType as keyof typeof KeyTypes] }
                    </Typography>
                  </Grid>
                  <Grid item xs={1}>
                    <Typography>
                      <NumberFormat 
                        value={item.price}
                        displayType={'text'} 
                        thousandSeparator={true} 
                        prefix={'$'} 
                        decimalScale={2}
                        fixedDecimalScale={true}
                      />    
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2">
                      {item.report}
                    </Typography>
                  </Grid>
                </Grid>
              ))} 
            </CardContent>
          </Card> 
        }
        { vinError &&
          <div className={classes.alert}>
            <Alert 
              aria-label="vinError"
              severity="error"
            >
              Unable to decode VIN
            </Alert>
          </div>
        }
      </Grid>
    </Container>
  );
};

export default AdminSearch;
